import { isWithinInterval } from 'date-fns'
import { useMemo } from 'react'
import { FiltersT } from '..'
import useHospitalisationsData from '../apis/useHospitalisationsData'
import { GenericLineChart } from '../GenericLineChart'
import { parseDate, useNormalizedData } from '../helpers'
import { useOnDownload } from './useOnDownload'

export const HospitalisationsDashboard = ({
  filters,
  changeDateRange,
}: {
  filters: FiltersT
  changeDateRange: (start: string, end: string) => void
}) => {
  const { data, isFetched, isLoading, isError } = useHospitalisationsData()
  const onDownload = useOnDownload(data, filters, 'Hospitalisations')

  const normalizedData = useNormalizedData(data ?? [])

  const filterDateInterval = useMemo(
    () => ({
      start: parseDate(filters.dateRange.start),
      end: parseDate(filters.dateRange.end),
    }),
    [filters.dateRange.end, filters.dateRange.start]
  )

  // Filter in separate memos to increase performance
  // Start based on the one that's changed least
  const filteredDisease = useMemo(
    () =>
      normalizedData.filter((x) =>
        filters.dataType.map((y) => y.value).includes(x.diseaseName)
      ),
    [filters.dataType, normalizedData]
  )

  const filteredCountry = useMemo(
    () => filteredDisease.filter((x) => x.countryAlpha3 === filters.country),
    [filteredDisease, filters.country]
  )

  const filteredData = useMemo(
    () =>
      filteredCountry.filter((x) =>
        isWithinInterval(parseDate(x.date), filterDateInterval)
      ),
    [filterDateInterval, filteredCountry]
  )

  return (
    <GenericLineChart
      data={filteredData}
      title={`Hospitalisations`}
      yAxisTitle='Hospitalisations per 100k'
      dateRange={filters.dateRange}
      changeDateRange={changeDateRange}
      dataStatus={{ isFetched, isError, isLoading }}
      onDownload={onDownload}
    />
  )
}
